import React, { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { useHistory } from '@fs/zion-router'
import { trackEvent } from '@fs/zion-analytics'
import {
  Button,
  DialogOverlayActions,
  DialogOverlayFooter,
  useStatusOverlay,
  Row,
  Paragraph,
  Separator,
} from '@fs/zion-ui'
import { ContentDelete, ContentUpload, NoticeLoading } from '@fs/zion-icon'
import { removeGroupImage, setGroupImage } from '../../../services/groupservices'
import { MAX_FILE_SIZE, cropImage } from '../../../helpers/utilities'
import AspectRatioSVG from './AspectRatioSVG'

const cssImageUploader = css`
  display: none;
`

export default function ChangeGroupImageRouteView({
  groupDetails,
  onGroupImageChange,
  setUploadedImage,
  uploadedImage,
}) {
  const [t] = useTranslation()
  const history = useHistory()
  const [isUploading, setIsUploading] = useState(false)
  const showStatusOverlay = useStatusOverlay()

  const fileTooLargeMessage = t('change-group-image.error.file-size')
  const statusOverlayProps = useRef({
    message: t('change-group-image.error.not-able-to-upload'),
    type: 'error',
    dismissable: true,
    transient: true,
  })

  const handleDeleteImage = useCallback(() => {
    if (groupDetails) {
      // Whether user is non-admin or admin but last member, leave group
      removeGroupImage(groupDetails?.id).then(() => {
        trackEvent({ event_name: 'CreateGroup:REMOVE_IMAGE:CLICK' })
        onGroupImageChange?.(history)
      })
    } else {
      setUploadedImage(undefined)
      onGroupImageChange?.(history)
    }
  }, [groupDetails, history, onGroupImageChange, setUploadedImage])

  const handleChangeImage = useCallback((event) => {
    trackEvent({ event_name: 'EditGroup:UPLOAD_IMAGE:CLICK' })
    const fileUploader = document.getElementById('uploadGroupImage')
    // @ts-expect-error -- TODO: it seems like we don't need to pass in event.
    fileUploader.click(event)
  }, [])

  const handleFileChange = useCallback(
    async (event) => {
      const fileUpload = event.target
      const file = fileUpload.files?.[0]

      // If user navigates quickly, the code sometimes reaches here without a valid file
      if (!file) {
        setIsUploading(false)
        return
      }

      // Short-circuit process if file is bigger than 15MB
      if (file.size >= MAX_FILE_SIZE) {
        showStatusOverlay({ ...statusOverlayProps.current, message: fileTooLargeMessage })
        setIsUploading(false)
        return
      }

      const croppedFile = await cropImage(file)

      if (groupDetails?.id) {
        setGroupImage(groupDetails.id, croppedFile)
          .then(() => {
            onGroupImageChange?.(history)
          })
          .catch(() => showStatusOverlay(statusOverlayProps.current))
      } else {
        setIsUploading(false)
        setUploadedImage(croppedFile)
        onGroupImageChange?.(history)
      }
    },
    [fileTooLargeMessage, groupDetails?.id, history, onGroupImageChange, setUploadedImage, showStatusOverlay]
  )

  return (
    <>
      {isUploading && (
        <Row alignX="center">
          <NoticeLoading size="lg" />
        </Row>
      )}

      {/* This needs to be wrapped in !isUploading to prevent it from re-inserting this footer in the dialog while transitioning out after an image has uploaded */}
      {!isUploading && (
        <>
          <Paragraph size="sm" centered>
            {t('groups.upload-image.description', 'Please upload an image up to 15MB that has a 4:3 aspect ratio.')}
          </Paragraph>
          <Separator size="lg" />
          <Row alignX="center" alignY="middle">
            <AspectRatioSVG />
          </Row>
          <DialogOverlayFooter>
            <input
              type="file"
              accept="image/jpeg,image/png"
              id="uploadGroupImage"
              className={cssImageUploader}
              // onChange triggers AFTER the image is uploaded from the device
              onChange={handleFileChange}
              // onInput triggers BEFORE image starts uplaoding
              onInput={() => setIsUploading(true)}
            />
            <DialogOverlayActions>
              <Button
                disabled={!groupDetails?.imageUrl && !uploadedImage}
                emphasis="low"
                Icon={ContentDelete}
                onClick={handleDeleteImage}
              >
                {t('common-ui:delete.action')}
              </Button>
              <Button emphasis="high" Icon={ContentUpload} onClick={handleChangeImage}>
                {t('change-group-image.action.upload-image')}
              </Button>
            </DialogOverlayActions>
          </DialogOverlayFooter>
        </>
      )}
    </>
  )
}
